import { useState, useEffect, SetStateAction, Dispatch, useContext } from 'react';
import { useQuery } from 'react-query';
import {
  executeCodeSubmission,
  ASSIGNMENT_DETAILS_FETCH_REFRESH_TIME,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/helpers';
import { TabType } from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/types';
import { ExecuteSubmissionResponse } from '@scale/shared/sphereEngine/types';
import _ from 'lodash';
import { useCodeExecutionStateStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/stores/useCodeExecutionStateStore';
import {
  useChatStoreExtract,
  useCurrentStep,
} from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { SphereSubmissionType } from '@scale/shared/types/events/SphereSubmission';
import { ICustomCodeEnvironment } from '@scale/shared/types/config';
import API from '@scale/frontend-lib/dashboard-api';

interface CodeExecutionStateParams {
  storeKey: string;
  code: string;
  language: string;
  customCodeEnvironment?: ICustomCodeEnvironment | null;
  onExecutionComplete?: (data?: ExecuteSubmissionResponse) => void;
}

interface CodeExecutionStateResponse {
  outputTabOpen: boolean;
  outputValue: string | null;
  tab: TabType;
  setOutputTabOpen: (open: boolean) => void;
  setTab: (tab: TabType) => void;
  setOutputValue: Dispatch<SetStateAction<string | null>>;
  isFetching: boolean;
  submissionResponse?: ExecuteSubmissionResponse;
  fetchError: any;
  handleExecuteCode: () => void;
  loadingTaskIdForLogging: boolean;
}

const useCodeExecutionState = ({
  storeKey,
  code,
  language,
  customCodeEnvironment,
  onExecutionComplete,
}: CodeExecutionStateParams): CodeExecutionStateResponse => {
  const [outputValue, setOutputValue] = useState<string | null>(null);
  const [hasFetchedOnMount, setHasFetchedOnMount] = useState<boolean>(false);

  // Output tab state is managed in an external store
  const { setCodeExecutionState, getCodeExecutionState } = useCodeExecutionStateStore();

  const [outputTabOpen, setOutputTabOpen] = [
    getCodeExecutionState(storeKey).outputTabOpen ?? false,
    (open: boolean) => setCodeExecutionState(storeKey, { outputTabOpen: open }),
  ];

  const [tab, setTab] = [
    getCodeExecutionState(storeKey).tab ?? TabType.STDOUT,
    (tab: TabType) => setCodeExecutionState(storeKey, { tab }),
  ];

  const taskContext = useContext(ChatTaskContext);
  const step = useCurrentStep();
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);

  const { isFetching: loadingTaskIdForLogging, data: assignmentDetails } = useQuery(
    ['assignmentDetails', taskContext.assignmentId],
    async () => {
      const assignmentDetails = await API.genai.getAssignmentDetails({
        assignmentId: taskContext.assignmentId ?? '',
      });
      return assignmentDetails;
    },
    {
      enabled: !!taskContext.assignmentId,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
      staleTime: ASSIGNMENT_DETAILS_FETCH_REFRESH_TIME,
    },
  );

  const logData = {
    taskId: taskContext.taskId ?? assignmentDetails?.taskId,
    projectId: taskContext.projectId,
    assignmentId: taskContext.assignmentId,
    workerId: taskContext.workerId,
    stepId: step?.id,
    stepType: step?.type,
    reviewLevel,
    timestamp: new Date(),
    submissionType: SphereSubmissionType.ExecutableCodeBlock,
  };

  const {
    isFetching,
    data: submissionResponse,
    error: fetchError,
    refetch: handleExecuteCode,
  } = useQuery(
    [storeKey],
    async () => {
      const submissionResponse = await executeCodeSubmission(
        code,
        language,
        logData,
        taskContext.taskId ?? taskContext.assignmentId ?? assignmentDetails?.taskId,
        customCodeEnvironment,
      );
      return submissionResponse;
    },
    {
      enabled: false,
      onSettled: data => {
        handleOutputDisplay(true, data);
        onExecutionComplete && onExecutionComplete(data);
      },
    },
  );

  // When the component mounts, we should display the output automatically if it's been cached
  useEffect(() => {
    if (submissionResponse && !hasFetchedOnMount) {
      setTab(
        _.isNil(submissionResponse?.stderr) && _.isNil(submissionResponse?.stdout)
          ? TabType.STDOUT
          : submissionResponse?.stderr
          ? TabType.STDERR
          : TabType.STDOUT,
      );
      setOutputValue(
        _.isNil(submissionResponse?.stderr) && _.isNil(submissionResponse?.stdout)
          ? ''
          : submissionResponse?.stderr ?? submissionResponse?.stdout,
      );
      setHasFetchedOnMount(true);
    }
  }, [submissionResponse]);

  const handleSwitchTab = (tab: TabType) => {
    if (tab === TabType.STDOUT) {
      setTab(TabType.STDOUT);
      setOutputValue(submissionResponse?.stdout ?? null);
    } else if (tab === TabType.STDERR) {
      setTab(TabType.STDERR);
      setOutputValue(submissionResponse?.stderr ?? null);
    }
  };

  const handleOutputDisplay = (openOutputTab: boolean, data?: ExecuteSubmissionResponse) => {
    // If there's no execution data, we open the stdout tab
    if (_.isNil(data?.stderr) && _.isNil(data?.stdout)) {
      setTab(TabType.STDOUT);
      setOutputValue('');
      openOutputTab && setOutputTabOpen(true);
      return;
    }

    // If there's execution data, we give preference to stderr over stdout data
    if (!_.isNil(data?.stderr)) {
      setTab(TabType.STDERR);
      setOutputValue(data.stderr);
      openOutputTab && setOutputTabOpen(true);
    } else if (!_.isNil(data?.stdout)) {
      setTab(TabType.STDOUT);
      setOutputValue(data.stdout);
      openOutputTab && setOutputTabOpen(true);
    }
  };

  return {
    outputTabOpen,
    outputValue,
    tab,
    setOutputTabOpen,
    setTab: handleSwitchTab,
    setOutputValue,
    isFetching,
    submissionResponse,
    fetchError,
    handleExecuteCode,
    loadingTaskIdForLogging,
  };
};

export default useCodeExecutionState;
